import {
  Modal,
  MessageModalLayout,
  Box,
  Button,
  TextButton,
  Heading,
} from '@wix/design-system';
import { useExperiments, useTranslation } from '@wix/fe-essentials-standalone';
import React, { FC, useState } from 'react';
import MobileFullScreenModalLayout from '@components/MobileFullScreenModalLayout';
import CustomCodeConsentContent from './CustomCodeConsentContent';
import { ENABLE_VELO_MODAL_NEW_CONTENT } from '@utils/constants';
import CustomCodeConsentNewContent from './CustomCodeConsentNewContent';

const ConsentModalBiClickTypes = {
  close: 'velo / blocks consent modal - close',
  continue: 'velo / blocks consent modal - continue to checkout',
  cancel: 'velo / blocks consent modal - cancel',
} as const;

type ConsentClickTypes =
  (typeof ConsentModalBiClickTypes)[keyof typeof ConsentModalBiClickTypes];

export interface ICustomCodeConsentModalProps {
  isOpen: boolean;
  onClose: () => void;
  callback: () => void;
  logBiClick: (clickType: ConsentClickTypes) => void;
  isMobileView?: boolean;
  privacyPolicyUrl?: string;
}

const CustomCodeConsentModal: FC<ICustomCodeConsentModalProps> = ({
  isOpen,
  onClose,
  callback,
  logBiClick,
  isMobileView,
  privacyPolicyUrl,
}) => {
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [hasError, setHasError] = useState(false);

  const toggleAcceptTerms = () => {
    setHasAcceptedTerms((current) => !current);
    setHasError(false);
  };

  const onCloseClick = () => {
    logBiClick?.(ConsentModalBiClickTypes.close);
    onClose();
  };

  const onCancelClick = () => {
    logBiClick?.(ConsentModalBiClickTypes.cancel);
    onClose();
  };

  const onPrimaryClick = () => {
    if (hasAcceptedTerms) {
      logBiClick?.(ConsentModalBiClickTypes.continue);
      callback();
    } else {
      setHasError(true);
    }
  };

  const props: IConsentModalContentProps = {
    toggleAcceptTerms,
    onCloseClick,
    onCancelClick,
    onPrimaryClick,
    hasAcceptedTerms,
    hasError,
    privacyPolicyUrl,
  };

  return (
    <Modal isOpen={isOpen}>
      {isMobileView ? (
        <MobileConsentModalLayout {...props} />
      ) : (
        <ConsentModalLayout {...props} />
      )}
    </Modal>
  );
};

interface IConsentModalContentProps {
  onCancelClick: () => void;
  onPrimaryClick: () => void;
  onCloseClick: () => void;
  toggleAcceptTerms: () => void;
  hasAcceptedTerms: boolean;
  hasError: boolean;
  privacyPolicyUrl?: string;
}

const ConsentModalLayout: FC<IConsentModalContentProps> = ({
  onPrimaryClick,
  onCancelClick,
  onCloseClick,
  toggleAcceptTerms,
  hasAcceptedTerms,
  hasError,
  privacyPolicyUrl,
}) => {
  const { t } = useTranslation();
  const { experiments } = useExperiments({ readOnly: true });
  const useVeloModalNewContent = experiments.enabled(
    ENABLE_VELO_MODAL_NEW_CONTENT,
  );

  return (
    <MessageModalLayout
      onCloseButtonClick={onCancelClick}
      primaryButtonOnClick={onPrimaryClick}
      primaryButtonText={t(
        'marketplace.template-page.custom-code-consent-modal.primary-cta',
      )}
      secondaryButtonOnClick={onCloseClick}
      secondaryButtonText={t(
        'marketplace.template-page.custom-code-consent-modal.secondary-cta',
      )}
      title={
        <Heading size="large">
          {useVeloModalNewContent
            ? t('marketplace.template-redemption.consent-new-content.title')
            : t('marketplace.template-page.custom-code-consent-modal.title')}
        </Heading>
      }
      content={
        useVeloModalNewContent ? (
          <CustomCodeConsentNewContent
            onClick={toggleAcceptTerms}
            isChecked={hasAcceptedTerms}
            hasError={hasError}
            privacyPolicyUrl={privacyPolicyUrl}
          />
        ) : (
          <CustomCodeConsentContent
            onClick={toggleAcceptTerms}
            isChecked={hasAcceptedTerms}
            hasError={hasError}
          />
        )
      }
    />
  );
};

const MobileConsentModalLayout: FC<IConsentModalContentProps> = ({
  onPrimaryClick,
  onCancelClick,
  onCloseClick,
  toggleAcceptTerms,
  hasAcceptedTerms,
  hasError,
  privacyPolicyUrl,
}) => {
  const { t } = useTranslation();
  const { experiments } = useExperiments({ readOnly: true });
  const useVeloModalNewContent = experiments.enabled(
    ENABLE_VELO_MODAL_NEW_CONTENT,
  );

  return (
    <MobileFullScreenModalLayout
      title={
        useVeloModalNewContent
          ? t('marketplace.template-redemption.consent-new-content.title')
          : t('marketplace.template-page.custom-code-consent-modal.title')
      }
      content={
        useVeloModalNewContent ? (
          <CustomCodeConsentNewContent
            onClick={toggleAcceptTerms}
            isChecked={hasAcceptedTerms}
            isMobile={true}
            hasError={hasError}
            privacyPolicyUrl={privacyPolicyUrl}
          />
        ) : (
          <CustomCodeConsentContent
            onClick={toggleAcceptTerms}
            isChecked={hasAcceptedTerms}
            isMobile={true}
            hasError={hasError}
          />
        )
      }
      footer={
        <Box direction="vertical" gap="18px" align="center" padding="24px">
          <Button size="medium" onClick={onPrimaryClick}>
            {t(
              'marketplace.template-page.custom-code-consent-modal.primary-cta',
            )}
          </Button>
          <TextButton onClick={onCancelClick}>
            {t(
              'marketplace.template-page.custom-code-consent-modal.secondary-cta',
            )}
          </TextButton>
        </Box>
      }
      onClose={onCloseClick}
    />
  );
};

export default CustomCodeConsentModal;
